
  class DogCartPhoto extends HTMLElement {
    connectedCallback() {
      if (this._wired) return;
      this.trigger = this.querySelector('[data-dog-open]');
      this.dialog = this.querySelector('[data-dog-dialog]');
      this.input = this.querySelector('[data-dog-input]');
      this.chooseBtn = this.querySelector('[data-dog-choose]');
      this.selected = this.querySelector('[data-dog-selected]');
      this.preview = this.querySelector('[data-dog-preview]');
      this.filename = this.querySelector('[data-dog-filename]');
      this.errorEl = this.querySelector('[data-dog-error]');
      this.saveBtn = this.querySelector('[data-dog-save]');
      this.spinner = this.querySelector('[data-dog-spinner]');
      this.cancelBtns = Array.from(this.querySelectorAll('[data-dog-cancel]'));
      if (!this.trigger || !this.dialog || !this.input || !this.saveBtn) return;
      this._wired = true;

      this.trigger.addEventListener('click', () => this.openDialog());
      if (this.chooseBtn) this.chooseBtn.addEventListener('click', () => this.input.click());
      this.input.addEventListener('change', () => this.onFile());
      this.saveBtn.addEventListener('click', () => this.save());
      this.cancelBtns.forEach((b) => b.addEventListener('click', () => this.closeDialog()));
      this.dialog.addEventListener('click', (e) => { if (e.target === this.dialog) this.closeDialog(); });
      this.dialog.addEventListener('cancel', (e) => { if (this.busy) e.preventDefault(); });
    }

    openDialog() {
      this.clearError();
      if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
      else this.dialog.setAttribute('open', '');
    }

    closeDialog() {
      if (this.busy) return;
      if (typeof this.dialog.close === 'function') this.dialog.close();
      else this.dialog.removeAttribute('open');
    }

    showError(msg) { if (this.errorEl) { this.errorEl.textContent = msg; this.errorEl.classList.remove('hidden'); } }
    clearError() { if (this.errorEl) { this.errorEl.textContent = ''; this.errorEl.classList.add('hidden'); } }

    onFile() {
      this.clearError();
      const file = this.input.files && this.input.files[0];
      this.saveBtn.disabled = true;
      if (this.selected) this.selected.classList.add('hidden');
      if (!file) return;

      const maxBytes = (parseInt(this.input.dataset.maxMb, 10) || 20) * 1024 * 1024;
      if (file.type && file.type.indexOf('image/') !== 0) {
        this.input.value = '';
        this.showError('Please choose an image file (JPG, PNG, etc.).');
        return;
      }
      if (file.size > maxBytes) {
        this.input.value = '';
        this.showError('That image is over 20 MB. Please choose a smaller photo.');
        return;
      }
      if (this.filename) this.filename.textContent = file.name;
      if (this.preview) {
        const reader = new FileReader();
        reader.onload = (e) => { this.preview.src = e.target.result; };
        reader.readAsDataURL(file);
      }
      if (this.selected) this.selected.classList.remove('hidden');
      this.saveBtn.disabled = false;
    }

    setBusy(state) {
      this.busy = state;
      const hasFile = !!(this.input.files && this.input.files[0]);
      this.saveBtn.disabled = state || !hasFile;
      if (this.spinner) this.spinner.classList.toggle('hidden', !state);
      this.cancelBtns.forEach((b) => { b.disabled = state; });
    }

    async save() {
      const file = this.input.files && this.input.files[0];
      if (!file) return;
      this.clearError();
      this.setBusy(true);
      try {
        const variantId = this.dataset.variantId;
        const quantity = this.dataset.quantity || '1';
        const lineKey = this.dataset.lineKey;
        const photoName = this.dataset.photoName;
        // Liquid `properties | json` serializes as an array of [key, value] pairs.
        let props = [];
        try { props = JSON.parse(this.dataset.properties || '[]'); } catch (e) { props = []; }
        if (!Array.isArray(props)) props = [];

        // Detect context from this element's own ancestor — the cart drawer also
        // exists in the layout on the cart page, so "does a drawer exist" is not enough.
        const hostDrawerItems = this.closest('cart-drawer-items');
        const hostPageItems = this.closest('cart-items-component') || document.querySelector('cart-items-component');
        const inDrawer = !!hostDrawerItems;
        const cartDrawer = document.querySelector('cart-drawer');

        const sectionIds = [];
        const addSectionId = (id) => { if (id && sectionIds.indexOf(id) === -1) sectionIds.push(id); };
        if (!inDrawer && hostPageItems && hostPageItems.dataset.sectionId) {
          addSectionId(hostPageItems.dataset.sectionId); // the visible cart-page table
        }
        if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
          cartDrawer.getSectionsToRender().forEach((s) => addSectionId(s.id)); // drawer + cart-icon-bubble
        } else if (hostDrawerItems && hostDrawerItems.dataset.sectionId) {
          addSectionId(hostDrawerItems.dataset.sectionId);
        }

        // 1) Add a new line that carries the uploaded file (multipart).
        const addData = new FormData();
        addData.append('id', variantId);
        addData.append('quantity', quantity);
        props.forEach((pair) => {
          if (!Array.isArray(pair) || pair.length < 2) return;
          const k = pair[0];
          const v = pair[1];
          if (k === photoName) return;                                   // replaced by the new file below
          if (typeof k !== 'string' || k === '' || k.charAt(0) === '_') return; // skip hidden/internal markers
          if (/^\d+$/.test(k)) return;                                   // skip bogus numeric-only keys
          if (v == null || v === '' || typeof v !== 'string') return;
          addData.append('properties[' + k + ']', v);
        });
        addData.append('properties[' + photoName + ']', file);
        addData.append('properties[_dog_photo_session]', Date.now() + '-' + Math.floor((window.performance && performance.now ? performance.now() : 0)));

        const addCfg = fetchConfig('javascript');
        addCfg.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete addCfg.headers['Content-Type'];
        addCfg.body = addData;

        const addResp = await fetch(`${routes.cart_add_url}`, addCfg).then((r) => r.json());
        if (addResp.status || addResp.errors) {
          throw new Error(addResp.description || addResp.message || 'Could not add the photo.');
        }

        // 2) Remove the original (photo-less / old-photo) line and request refreshed sections.
        const changeBody = { id: lineKey, quantity: 0 };
        if (sectionIds.length) {
          changeBody.sections = sectionIds.join(',');
          changeBody.sections_url = window.location.pathname;
        }
        const changeResp = await fetch(`${routes.cart_change_url}`, {
          ...fetchConfig('json'),
          body: JSON.stringify(changeBody),
        }).then((r) => r.json());

        // 3) Refresh the cart UI for the correct context.
        if (inDrawer) {
          if (cartDrawer && typeof cartDrawer.renderContents === 'function' && changeResp.sections && changeResp.sections['cart-drawer']) {
            cartDrawer.renderContents(changeResp);
          } else if (typeof hostDrawerItems.updateSections === 'function') {
            hostDrawerItems.updateSections(changeResp.sections);
          } else {
            window.location.reload();
            return;
          }
        } else {
          // Cart page: the add+remove already succeeded server-side, so reload to
          // show the authoritative cart. Partial section-replace here proved unreliable
          // (it left the original line visible until reload).
          window.location.reload();
          return;
        }

        const countEls = document.querySelectorAll('[data-cart-count]');
        if (changeResp.item_count !== undefined) {
          countEls.forEach((el) => { el.textContent = changeResp.item_count; });
        }
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: changeResp }));
      } catch (err) {
        this.setBusy(false);
        this.showError(err && err.message ? err.message : 'Something went wrong. Please try again.');
      }
    }
  }

  if (!customElements.get('dog-cart-photo')) customElements.define('dog-cart-photo', DogCartPhoto);
